<template>
  <div class="d-block w-100" :data-cy="`attachment-download-${attachment.name}`">
    <!-- UPLOADING FILE -->
    <b-button v-if="progress && !sendFailed"
              class="p-0 text-left mw-100 position-relative cancel-upload-btn"
              variant="transparent"
    >
      <div class="d-flex align-items-center mb-1">
        <span class="position-relative extension-img d-flex align-items-center justify-content-around">
          <svg
            role="img"
            aria-hidden="true"
            width="32"
            height="32"
            viewBox="0 0 36 36"
            class="circular-chart spinning-indicator"
            :data-cy="`attachment-${attachment.name}-upload-progress-spinner`"
          >
            <path class="circle-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
            <path
              class="circle"
              :stroke-dasharray="`${progress}, 100`"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            />
          </svg>
        </span>
        <div class="ml-2 info-wrapper">
          <p class="title mb-0 font-weight-normal d-inline-block text-truncate w-100">{{ attachment.name }}</p>
          <p class="description small gray-300 mb-0">{{ progress }}%</p>
        </div>
      </div>
    </b-button>
    <!-- SEND FAILED -->
    <b-button v-else-if="sendFailed"
              class="p-0 text-left mw-100"
              variant="transparent"
    >
      <div class="d-flex align-items-center">
        <img :src="image"
             class="extension-img"
             :data-cy="`attachment-${attachment.name}-extension-icon`"
             alt=""
             loading="lazy"
        />
        <div class="ml-2 info-wrapper">
          <p class="title mb-0 font-weight-normal d-inline-block text-truncate w-100">{{ attachment.name }}</p>
          <p class="description small gray-300 mb-0" :data-cy="`attachment-${attachment.name}-size`">
            {{ $getSize(attachment.size, false) }}
          </p>
        </div>
      </div>
    </b-button>
    <!-- UPLOADED FILE -->
    <SLink
      v-else
      appearance="b-link"
      class="p-0 d-flex align-items-center attachment-button-link"
      data-cy="downloadable-attachment"
      :download="attachment ? attachment.name : ''"
      force-hide-download-icon
      href="#"
      target="_blank"
      :disabled="$wait.waiting(`list-item-${attachment.uuid}.${key}`)"
      :class="{ active: attachment.extension !== '' }"
      @click="handleAttachment"
    >
      <img :src="image"
           class="extension-img"
           alt=""
           loading="lazy"
      />
      <div class="ml-2 info-wrapper overflow-hidden">
        <span class="sr-only">{{ $t('accessibility.external_url') }} {{ $t('accessibility.download_file') }} "{{ attachment.name }}"
          {{ $getFileTypeAndSize(attachment) }}</span>
        <p aria-hidden="true" class="title mb-0 font-weight-normal text-truncate">{{ attachment.name }}</p>
        <div class="d-flex align-items-center small gray-300">
          <p aria-hidden="true" class="description mb-0">{{ $getSize(attachment.size, false) }}</p>
          <sdui-icon class="icon-download-o ml-1 small" />
        </div>
      </div>
      <sdui-loader v-if="$wait.waiting(`list-item-${attachment.uuid}.${key}`)" class="extension-img p2 d-inline-flex ml-1 h-auto" />
    </SLink>
  </div>
</template>

<script>
import fileMixin from 'src/mixins/channels/fileMixin.js'
import SduiLoader from 'src/components/miscellaneous/ui/common/SduiLoader.vue'

export default {
  name: 'SduiAttachment',
  components: { SduiLoader },
  mixins: [fileMixin],
  props: {
    attachment: {
      type: Object,
      default: () => ({}),
    },

    progress: {
      type: Number,
      default: 0,
    },

    sendFailed: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    image() {
      return this.getIconImageByFile(this.attachment)
    },

    key() {
      return this.$uuid()
    },
  },

  methods: {
    async handleAttachment() {
      await this.handleDownload(`list-item-${this.attachment.uuid}.${this.key}`)
    },
  },

  watch: {
    attachment: {
      handler(attachment) {
        if (!attachment) return
        this.file = attachment
      },

      immediate: true,
    },
  },
}
</script>

<style scoped lang="scss">
.extension-img {
  min-width: 36px;
  min-height: 36px;
}

.info-wrapper {
  max-width: 85%;
}

.cancel-upload-btn {
  .circle-bg {
    fill: none;
    stroke: $color-primary;
    opacity: 0.15 !important;
    stroke-width: 2;
  }

  .circle {
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    transition: 0.1s;
    stroke: $color-primary;
  }

  .icon {
    left: 0;
    top: 0;
  }
}

.attachment-button-link {
  color: inherit;
}
</style>
